import airportsData from "./airports.json";

// ✅ Priority List of Airports to Show at the Top
export const PRIORITY_AIRPORTS = new Set([
  "TIA", // Tirana
  "SKP", // Skopje
  "PRN", // Pristina
  "DUS", // Düsseldorf
  "FRA", // Frankfurt
  "BER", // Berlin
  "MUC", // Munich
  "ZRH", // Zurich
  "GVA", // Geneva
  "BSL", // Basel
]);

// Albanian alternative city names mapping
export const ALTERNATIVE_CITY_NAMES = {
  "MILANO": "MILAN",
  "BOLONJA": "BOLOGNA", 
  "BOLOGONA": "BOLOGNA",
  "BAZEL": "BASEL",
  "STAMBOLL": "ISTANBUL",
  "VENECIA": "VENICE",
  "LONDER": "LONDON",
  "ATHINE": "ATHENS",
  "BARCELLONA": "BARCELONA",
  "TORINO": "TURIN",
  "ABU DABI": "ABU DHABI",
  "DYSELDORF": "DUSSELDORF",
  "MYNHEN": "MUNICH",
  "KOPENHAGE": "COPENHAGEN",
  "BORDO": "BORDEAUX",
  "ZYRIH": "ZURICH",
  "GJENEVE": "GENEVA",
  "MEMINGEN": "MEMMINGEN",
  "GOTEBORG": "GOTHENBURG",
  "STOKHOLM": "STOCKHOLM",
  "VJENE": "VIENNA",
  "BRATISLLAVE": "BRATISLAVA",
  "VARSHAVE": "WARSAW",
  "TARANA": "TIRANA",
  "PRISTINA": "PRISHTINA",
  "SHTUTGART": "STUTTGART"
};

// ✅ Transform JSON data to normalized array format
const normalizeAirportData = () => {
  const airports = [];
  
  Object.entries(airportsData).forEach(([key, airport]) => {
    // Skip entries without proper IATA codes (focus on commercial airports)
    if (!airport.iata || airport.iata.length !== 3) {
      return;
    }
    
    const normalizedAirport = {
      id: airport.iata,
      icao: airport.icao,
      name: airport.name,
      city_name: airport.city,
      state: airport.state || "",
      country_code: airport.country,
      country_name: getCountryName(airport.country),
      area_code: airport.iata,
      latitude: airport.lat,
      longitude: airport.lon,
      elevation: airport.elevation,
      timezone: airport.tz,
      // Add search-friendly versions
      searchableText: `${airport.iata} ${airport.name} ${airport.city} ${airport.country}`.toLowerCase(),
      alternative_city_name: []
    };
    
    airports.push(normalizedAirport);
  });
  
  return airports;
};

// ✅ Country code to name mapping (add more as needed)
const getCountryName = (code) => {
  const countryMap = {
    'US': 'United States',
    'GB': 'United Kingdom', 
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy',
    'ES': 'Spain',
    'NL': 'Netherlands',
    'CH': 'Switzerland',
    'AT': 'Austria',
    'BE': 'Belgium',
    'AL': 'Albania',
    'MK': 'North Macedonia',
    'XK': 'Kosovo',
    'TR': 'Turkey',
    'GR': 'Greece'
  };
  return countryMap[code] || code;
};

// ✅ Initialize normalized airports
export const airports = normalizeAirportData();

// ✅ Add alternative names to airports
const enrichAirportsWithAlternativeNames = () => {
  const reverseMap = {};
  Object.entries(ALTERNATIVE_CITY_NAMES).forEach(([alternativeName, standardName]) => {
    if (!reverseMap[standardName]) {
      reverseMap[standardName] = [];
    }
    reverseMap[standardName].push(alternativeName);
  });
  
  airports.forEach(airport => {
    const cityNameUpper = airport.city_name.toUpperCase();
    if (reverseMap[cityNameUpper]) {
      airport.alternative_city_name = reverseMap[cityNameUpper];
    }
  });
  
};

// Execute enrichment
enrichAirportsWithAlternativeNames();

// ✅ Create optimized lookup maps
const createLookupMaps = () => {
  const airportMap = new Map();
  const cityMap = new Map();
  const countryMap = new Map();
  
  airports.forEach((airport) => {
    const lowerCaseId = airport.id.toLowerCase();
    const lowerCaseName = airport.name.toLowerCase();
    const lowerCaseCity = airport.city_name.toLowerCase();
    const lowerCaseCountry = airport.country_code.toLowerCase();
    
    // Main lookups
    airportMap.set(lowerCaseId, airport);
    airportMap.set(lowerCaseName, airport);
    airportMap.set(lowerCaseCity, airport);
    
    // City grouping for better UX
    if (!cityMap.has(lowerCaseCity)) {
      cityMap.set(lowerCaseCity, []);
    }
    cityMap.get(lowerCaseCity).push(airport);
    
    // Country grouping
    if (!countryMap.has(lowerCaseCountry)) {
      countryMap.set(lowerCaseCountry, []);
    }
    countryMap.get(lowerCaseCountry).push(airport);
    
    // ICAO code
    if (airport.icao) {
      airportMap.set(airport.icao.toLowerCase(), airport);
    }
    
    // Alternative city names
    if (airport.alternative_city_name && airport.alternative_city_name.length > 0) {
      airport.alternative_city_name.forEach(name => {
        airportMap.set(name.toLowerCase(), airport);
      });
    }
  });
  
  return { airportMap, cityMap, countryMap };
};

const { airportMap, cityMap, countryMap } = createLookupMaps();

// ✅ Enhanced Trie Implementation with scoring
class TrieNode {
  constructor() {
    this.children = {};
    this.isEndOfWord = false;
    this.airports = new Set(); // Can store multiple airports for same prefix
    this.score = 0; // For ranking results
  }
}

class AirportTrie {
  constructor() {
    this.root = new TrieNode();
  }

  insert(airport) {
    const searchTerms = [
      { text: airport.id, weight: 10 }, // IATA codes get highest weight
      { text: airport.icao, weight: 8 },
      { text: airport.city_name, weight: 7 },
      { text: airport.name, weight: 5 },
      { text: airport.country_name, weight: 2 },
      ...(airport.alternative_city_name || []).map(name => ({ text: name, weight: 6 }))
    ];

    searchTerms.forEach(({ text, weight }) => {
      if (!text) return;
      
      const normalizedText = text.toLowerCase().trim();
      let node = this.root;
      
      for (let char of normalizedText) {
        if (!node.children[char]) {
          node.children[char] = new TrieNode();
        }
        node = node.children[char];
        node.airports.add(airport);
        node.score = Math.max(node.score, weight);
      }
      
      node.isEndOfWord = true;
    });
  }

  search(prefix, limit = 50) {
    if (!prefix || prefix.length < 1) return [];
    
    let node = this.root;
    const lowerPrefix = prefix.toLowerCase().trim();
    
    for (let char of lowerPrefix) {
      if (!node.children[char]) {
        return [];
      }
      node = node.children[char];
    }
    
    return this._collectAirports(node, limit, lowerPrefix);
  }

  _collectAirports(node, limit, searchTerm) {
    const results = new Map(); // Use Map to avoid duplicates and store scores
    
    const traverse = (currentNode, depth = 0) => {
      if (results.size >= limit) return;
      
      if (currentNode.airports.size > 0) {
        currentNode.airports.forEach(airport => {
          if (!results.has(airport.id)) {
            // Calculate relevance score
            let score = currentNode.score || 1;
            
            // Boost exact matches
            if (airport.id.toLowerCase() === searchTerm) score += 50;
            if (airport.city_name.toLowerCase() === searchTerm) score += 30;
            if (airport.name.toLowerCase().includes(searchTerm)) score += 10;
            
            // Boost priority airports
            if (PRIORITY_AIRPORTS.has(airport.id)) score += 100;
            
            // Boost by prefix match quality (earlier = better)
            score += Math.max(0, 10 - depth);
            
            results.set(airport.id, { airport, score });
          }
        });
      }
      
      // Continue traversing children
      Object.values(currentNode.children).forEach(child => {
        traverse(child, depth + 1);
      });
    };
    
    traverse(node);
    
    // Sort by score (highest first) and return airports
    return Array.from(results.values())
      .sort((a, b) => b.score - a.score)
      .map(({ airport }) => airport)
      .slice(0, limit);
  }
}

// ✅ Initialize Trie with all airports
const trie = new AirportTrie();
airports.forEach(airport => trie.insert(airport));

// ✅ Utility function to convert country code to flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🏳️';
  
  // Special handling for non-standard country codes
  const specialFlags = {
    'XK': '🇽🇰', // Kosovo flag (alternative code)
    'KS': '🇽🇰', // Kosovo flag (actual code used in airport data)
    'XX': '🏳️', // Unknown
    'EU': '🇪🇺', // European Union
    'UN': '🇺🇳', // United Nations
  };
  
  const upperCode = countryCode.toUpperCase();
  if (specialFlags[upperCode]) {
    return specialFlags[upperCode];
  }
  
  // Generate flag emoji for standard ISO codes
  try {
    const codePoints = upperCode
      .split('')
      .map(char => char.charCodeAt(0) + 0x1F1A5);
    
    return String.fromCodePoint(...codePoints);
  } catch (error) {
    console.warn(`Could not generate flag for country code: ${countryCode}`);
    return '🏳️'; // Fallback to white flag
  }
};

// ✅ Enhanced airport search with grouped results for better UX
export const searchAirports = async (searchTerm, options = {}) => {
  const {
    limit = 20,
    includeCoordinates = false,
    countryFilter = null,
    minifyResults = true,
    groupResults = false
  } = options;
  
  if (!searchTerm || searchTerm.trim().length < 1) {
    // Return priority airports when no search term
    const results = airports
      .filter(airport => PRIORITY_AIRPORTS.has(airport.id))
      .slice(0, limit)
      .map(airport => minifyResults ? minifyAirport(airport) : airport);
    
    return groupResults ? { groups: [{ type: 'popular', title: 'Popular Airports', results }] } : results;
  }
  
  const cleanSearchTerm = searchTerm.trim().toLowerCase();
  const searchTermUpper = cleanSearchTerm.toUpperCase();
  
  // If groupResults is requested, return categorized results
  if (groupResults) {
    return getGroupedSearchResults(cleanSearchTerm, searchTermUpper, { limit, countryFilter, minifyResults, includeCoordinates });
  }
  
  // Original flat search for backward compatibility
  let results = trie.search(cleanSearchTerm, limit * 2);
  
  if (countryFilter) {
    results = results.filter(airport => 
      airport.country_code.toLowerCase() === countryFilter.toLowerCase()
    );
  }
  
  const uniqueResults = removeDuplicates(results).slice(0, limit);
  
  return uniqueResults.map(airport => {
    const result = minifyResults ? minifyAirport(airport) : airport;
    
    if (includeCoordinates && airport.latitude && airport.longitude) {
      result.coordinates = {
        lat: airport.latitude,
        lng: airport.longitude
      };
    }
    
    return result;
  });
};

// ✅ New grouped search function for enhanced UX
const getGroupedSearchResults = (searchTerm, searchTermUpper, options) => {
  const { limit, countryFilter, minifyResults, includeCoordinates } = options;
  const groups = [];
  const usedAirportIds = new Set();
  
  // Group 1: Exact IATA code match (highest priority)
  const exactMatches = airports.filter(airport => {
    if (countryFilter && airport.country_code.toLowerCase() !== countryFilter.toLowerCase()) return false;
    return airport.id === searchTermUpper && !usedAirportIds.has(airport.id);
  });
  
  if (exactMatches.length > 0) {
    exactMatches.forEach(airport => usedAirportIds.add(airport.id));
    groups.push({
      type: 'exact_code',
      title: `Airport Code: ${searchTermUpper}`,
      results: exactMatches.slice(0, 3).map(airport => processAirportResult(airport, minifyResults, includeCoordinates))
    });
  }
  
  // Group 2: City name matches (including alternative names)
  const cityMatches = airports.filter(airport => {
    if (countryFilter && airport.country_code.toLowerCase() !== countryFilter.toLowerCase()) return false;
    if (usedAirportIds.has(airport.id)) return false;
    
    const cityName = airport.city_name.toLowerCase();
    const originalCityName = airport.original_city_name?.toLowerCase() || '';
    
    // Check exact city match or starts with search term
    return cityName === searchTerm || 
           cityName.startsWith(searchTerm) ||
           originalCityName === searchTerm ||
           originalCityName.startsWith(searchTerm) ||
           // Check alternative city names
           Object.keys(ALTERNATIVE_CITY_NAMES).some(altName => 
             altName.toLowerCase() === searchTerm && 
             ALTERNATIVE_CITY_NAMES[altName].toLowerCase() === cityName
           );
  });
  
  if (cityMatches.length > 0) {
    // Sort by exact match first, then alphabetically
    cityMatches.sort((a, b) => {
      const aExact = a.city_name.toLowerCase() === searchTerm;
      const bExact = b.city_name.toLowerCase() === searchTerm;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.city_name.localeCompare(b.city_name);
    });
    
    cityMatches.forEach(airport => usedAirportIds.add(airport.id));
    groups.push({
      type: 'city',
      title: `Airports in ${cityMatches[0].city_name}`,
      results: cityMatches.slice(0, 6).map(airport => processAirportResult(airport, minifyResults, includeCoordinates))
    });
  }
  
  // Group 3: Airport name matches
  const airportNameMatches = airports.filter(airport => {
    if (countryFilter && airport.country_code.toLowerCase() !== countryFilter.toLowerCase()) return false;
    if (usedAirportIds.has(airport.id)) return false;
    
    const airportName = airport.name.toLowerCase();
    return airportName.includes(searchTerm) || 
           // Check if search term matches any word in airport name
           searchTerm.split(' ').some(word => 
             word.length > 2 && airportName.includes(word)
           );
  });
  
  if (airportNameMatches.length > 0) {
    // Sort by relevance (earlier occurrence in name = higher relevance)
    airportNameMatches.sort((a, b) => {
      const aIndex = a.name.toLowerCase().indexOf(searchTerm);
      const bIndex = b.name.toLowerCase().indexOf(searchTerm);
      return aIndex - bIndex;
    });
    
    airportNameMatches.forEach(airport => usedAirportIds.add(airport.id));
    groups.push({
      type: 'airport_name',
      title: 'Airports by Name',
      results: airportNameMatches.slice(0, 5).map(airport => processAirportResult(airport, minifyResults, includeCoordinates))
    });
  }
  
  // Group 4: Country matches
  const countryMatches = airports.filter(airport => {
    if (countryFilter && airport.country_code.toLowerCase() !== countryFilter.toLowerCase()) return false;
    if (usedAirportIds.has(airport.id)) return false;
    
    const countryName = getCountryName(airport.country_code)?.toLowerCase() || '';
    return countryName.includes(searchTerm) ||
           airport.country_code.toLowerCase() === searchTerm;
  });
  
  if (countryMatches.length > 0 && searchTerm.length > 2) {
    // Prioritize major airports in the country
    countryMatches.sort((a, b) => {
      const aPriority = PRIORITY_AIRPORTS.has(a.id) ? 1 : 0;
      const bPriority = PRIORITY_AIRPORTS.has(b.id) ? 1 : 0;
      if (aPriority !== bPriority) return bPriority - aPriority;
      return a.city_name.localeCompare(b.city_name);
    });
    
    const countryName = getCountryName(countryMatches[0].country_code);
    countryMatches.forEach(airport => usedAirportIds.add(airport.id));
    groups.push({
      type: 'country',
      title: `Airports in ${countryName}`,
      results: countryMatches.slice(0, 4).map(airport => processAirportResult(airport, minifyResults, includeCoordinates))
    });
  }
  
  // Group 5: Fuzzy matches (partial matches)
  const fuzzyMatches = airports.filter(airport => {
    if (countryFilter && airport.country_code.toLowerCase() !== countryFilter.toLowerCase()) return false;
    if (usedAirportIds.has(airport.id)) return false;
    
    // Only include if search term is at least 3 characters
    if (searchTerm.length < 3) return false;
    
    const searchableText = `${airport.city_name} ${airport.name} ${airport.id}`.toLowerCase();
    
    // Check if all characters in search term appear in order (fuzzy match)
    let searchIndex = 0;
    for (let char of searchableText) {
      if (char === searchTerm[searchIndex]) {
        searchIndex++;
        if (searchIndex === searchTerm.length) return true;
      }
    }
    return false;
  });
  
  if (fuzzyMatches.length > 0 && groups.length > 0) { // Only show if we have other matches
    fuzzyMatches.forEach(airport => usedAirportIds.add(airport.id));
    groups.push({
      type: 'other',
      title: 'Other Matches',
      results: fuzzyMatches.slice(0, 3).map(airport => processAirportResult(airport, minifyResults, includeCoordinates))
    });
  }
  
  // Limit total results
  let totalResults = 0;
  const limitedGroups = groups.map(group => {
    const remainingLimit = Math.max(0, limit - totalResults);
    const limitedResults = group.results.slice(0, remainingLimit);
    totalResults += limitedResults.length;
    return { ...group, results: limitedResults };
  }).filter(group => group.results.length > 0);
  
  return {
    groups: limitedGroups,
    total: totalResults,
    searchTerm: searchTerm
  };
};

// ✅ Helper function to process airport results
const processAirportResult = (airport, minifyResults, includeCoordinates) => {
  const result = minifyResults ? minifyAirport(airport) : airport;
  
  // Add country flag
  result.countryFlag = getCountryFlag(airport.country_code);
  
  if (includeCoordinates && airport.latitude && airport.longitude) {
    result.coordinates = {
      lat: airport.latitude,
      lng: airport.longitude
    };
  }
  
  return result;
};

// ✅ Minify airport object for API responses
const minifyAirport = (airport) => ({
  id: airport.id,
  name: airport.name,
  city_name: airport.city_name,
  country_code: airport.country_code,
  area_code: airport.area_code
});

// ✅ Utility Functions
const removeDuplicates = (airports) => {
  const seen = new Set();
  return airports.filter(airport => {
    if (seen.has(airport.id)) {
      return false;
    }
    seen.add(airport.id);
    return true;
  });
};

// ✅ Get airport by exact code
export const getAirportByCode = (code) => {
  return airportMap.get(code.toLowerCase());
};

// ✅ Get airports by city
export const getAirportsByCity = (cityName) => {
  return cityMap.get(cityName.toLowerCase()) || [];
};

// ✅ Get airports by country
export const getAirportsByCountry = (countryCode) => {
  return countryMap.get(countryCode.toLowerCase()) || [];
};

// ✅ Calculate distance between airports (Haversine formula)
export const calculateDistance = (airport1, airport2) => {
  if (!airport1.latitude || !airport2.latitude) return null;
  
  const R = 6371; // Earth's radius in kilometers
  const dLat = (airport2.latitude - airport1.latitude) * Math.PI / 180;
  const dLon = (airport2.longitude - airport1.longitude) * Math.PI / 180;
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(airport1.latitude * Math.PI / 180) * Math.cos(airport2.latitude * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

// ✅ Get nearby airports
export const getNearbyAirports = (airport, radiusKm = 100, limit = 10) => {
  if (!airport.latitude || !airport.longitude) return [];
  
  return airports
    .map(otherAirport => ({
      airport: otherAirport,
      distance: calculateDistance(airport, otherAirport)
    }))
    .filter(({ airport: otherAirport, distance }) => 
      distance !== null && 
      distance <= radiusKm && 
      otherAirport.id !== airport.id
    )
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)
    .map(({ airport }) => airport);
};

// ✅ Debounce function for search input
export const debounce = (func, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ✅ Export stats for debugging
export const getSearchStats = () => ({
  totalAirports: airports.length,
  totalCountries: countryMap.size,
  totalCities: cityMap.size,
  priorityAirports: PRIORITY_AIRPORTS.size,
  alternativeNames: Object.keys(ALTERNATIVE_CITY_NAMES).length
});

